import { Button, Col, Divider, Empty, Form, Image, Input, InputNumber, Row, Select, Typography, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';
import UploadImg from '../../../../components/Upload/UploadImg';
import { useSingleService } from './SingleServiceStore';

const { Text } = Typography;

// YouTube havolasini frontda tekshirish
const YT_RE = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]+/i;

function AddRestTarif({ serviceId, data, onSuccess, onClose, simple = false }) {
    const { foodsList, halls, getFoodsList, getHalls, addServiceTariff, updateServiceTariff } = useSingleService();
    const [form] = Form.useForm();

    const [mainImg, setMainImg] = useState('');
    const [mealItems, setMealItems] = useState([]); // { foods_id, name, price, quantity }
    const [selectedHalls, setSelectedHalls] = useState([]);
    const [saving, setSaving] = useState(false);

    const isEdit = !!data;

    useEffect(() => {
        // Restoran bo'lmagan (simple) tariflarda taom/zal tanlash yo'q
        if (simple) return;
        getFoodsList();
        if (serviceId) getHalls(serviceId);
    }, [serviceId, getFoodsList, getHalls, simple]);

    // Tahrirlash rejimida mavjud qiymatlarni to'ldirish
    useEffect(() => {
        if (data) {
            form.setFieldsValue({ name: data.name, price: data.price, video_url: data.video_url, note: data.note });
            setMainImg(data.image || '');
            setMealItems((data.foods || []).map(f => ({
                foods_id: f.foods_id, name: f.name, price: Number(f.price) || 0, quantity: Number(f.quantity) || 1,
            })));
            setSelectedHalls(data.hall_ids || (data.halls || []).map(h => h.halls_id));
        }
    }, [data, form]);

    const foodOptions = useMemo(
        () => foodsList.map(f => ({
            value: f.id,
            label: f.category_name ? `${f.name} — ${f.category_name} (${Number(f.price || 0).toLocaleString()} so'm)` : `${f.name} (${Number(f.price || 0).toLocaleString()} so'm)`,
        })),
        [foodsList]
    );

    const hallOptions = useMemo(
        () => halls.map(h => ({ value: h.id, label: `${h.name}${h.places ? ` (${h.places} o'rin)` : ''}` })),
        [halls]
    );

    const handleMealAdd = (foodId) => {
        if (!foodId) return;
        if (mealItems.some(m => m.foods_id === foodId)) {
            message.info('Bu taom allaqachon qo\'shilgan');
            return;
        }
        const food = foodsList.find(f => f.id === foodId);
        if (!food) return;
        setMealItems(prev => [...prev, { foods_id: food.id, name: food.name, price: Number(food.price) || 0, quantity: 1 }]);
    };

    const handleQuantityChange = (foodId, quantity) => {
        setMealItems(prev => prev.map(item => item.foods_id === foodId ? { ...item, quantity: quantity || 1 } : item));
    };

    const handleRemoveMeal = (foodId) => {
        setMealItems(prev => prev.filter(item => item.foods_id !== foodId));
    };

    // Bir kishi uchun taomlar jami summasi
    const foodsTotal = mealItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

    const handleFinish = async (values) => {
        if (values.video_url && !YT_RE.test(values.video_url.trim())) {
            message.error("Faqat to'g'ri YouTube video havolasini kiriting");
            return;
        }
        const payload = {
            services_id: serviceId,
            name: values.name,
            price: values.price || 0,
            video_url: values.video_url || null,
            note: values.note || null,
            image: mainImg || null,
            foods: simple ? [] : mealItems.map(m => ({ foods_id: m.foods_id, quantity: m.quantity })),
            hall_ids: simple ? [] : selectedHalls,
        };
        setSaving(true);
        try {
            if (isEdit) {
                await updateServiceTariff(data.id, payload);
            } else {
                await addServiceTariff(payload);
            }
            onSuccess?.();
            onClose?.();
        } catch (e) {
            // xatolik store ichida ko'rsatiladi
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form form={form} layout='vertical' onFinish={handleFinish}>
            <Row gutter={15}>
                <Col span={12}>
                    <Form.Item label="Tarif nomi" name="name" rules={[{ required: true, message: 'Nomini kiriting' }]}>
                        <Input placeholder="Masalan: Standart paket" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label={simple ? "Narxi (so'm)" : "Narxi (1 kishi uchun, so'm)"} name="price" rules={[{ required: true, message: 'Narxni kiriting' }]}>
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            formatter={val => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                            parser={val => val.replace(/\s/g, '')}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <p style={{ color: 'green', marginTop: 0 }}>
                Tarifga oid video bo'lsa, uni avval YouTube'ga joylang va havolasini quyiga kiriting (faqat YouTube havolasi).
            </p>
            <Form.Item
                label="YouTube video havolasi"
                name="video_url"
                rules={[{ pattern: YT_RE, message: "To'g'ri YouTube havolasini kiriting" }]}
            >
                <Input placeholder="https://www.youtube.com/watch?v=..." />
            </Form.Item>

            <Form.Item label={simple ? "Rasm" : "Stol rasmi"}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <UploadImg imageUrl={mainImg} setImgurl={setMainImg} />
                    {mainImg && (
                        <Image src={process.env.REACT_APP_URL + mainImg} width={90} height={70} style={{ borderRadius: 8, objectFit: 'cover' }} />
                    )}
                </div>
            </Form.Item>

            <Form.Item label="Izoh (ixtiyoriy)" name="note">
                <Input.TextArea rows={2} placeholder="Tarif haqida qisqa izoh" />
            </Form.Item>

            {!simple && (
            <>
            <Divider orientation="left">Stolga 1 kishi uchun taomlar</Divider>
            <Row gutter={20}>
                <Col span={14}>
                    <Form.Item label="Taom qo'shish">
                        <Select
                            placeholder="Taomlar bo'limidan tanlang"
                            options={foodOptions}
                            onChange={handleMealAdd}
                            value={null}
                            showSearch
                            optionFilterProp="label"
                            notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Avval Taomlar bo'limiga taom qo'shing" />}
                        />
                    </Form.Item>

                    {mealItems.length === 0 && <Text type="secondary">Hali taom qo'shilmagan</Text>}
                    {mealItems.map(item => (
                        <Row key={item.foods_id} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                            <Col span={11}>
                                <Text>{item.name}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>{item.price.toLocaleString()} so'm</Text>
                            </Col>
                            <Col span={9}>
                                <InputNumber
                                    min={1}
                                    addonBefore="x"
                                    value={item.quantity}
                                    onChange={value => handleQuantityChange(item.foods_id, value)}
                                    style={{ width: '100%' }}
                                />
                            </Col>
                            <Col span={4} style={{ textAlign: 'right' }}>
                                <Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleRemoveMeal(item.foods_id)} />
                            </Col>
                        </Row>
                    ))}
                    {mealItems.length > 0 && (
                        <Text strong>Taomlar jami (1 kishi): {foodsTotal.toLocaleString()} so'm</Text>
                    )}
                </Col>
                <Col span={10}>
                    <Form.Item label="Zallar (tarif qaysi zallarga tegishli)">
                        <Select
                            mode="multiple"
                            placeholder="Zallarni tanlang"
                            options={hallOptions}
                            value={selectedHalls}
                            onChange={setSelectedHalls}
                            notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Avval Zallar qo'shing" />}
                        />
                    </Form.Item>
                </Col>
            </Row>
            </>
            )}

            <Form.Item style={{ marginTop: 12 }}>
                <Button type='primary' htmlType='submit' loading={saving}>
                    {isEdit ? 'Saqlash' : "Qo'shish"}
                </Button>
            </Form.Item>
        </Form>
    );
}

export default AddRestTarif;